import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import Konva from "konva";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { renderVizDslSlide } from "./VizDslRenderer";
import { renderDocDslDocument } from "./DocDslRenderer";
import { EXAMPLES } from "./VizDslExamples";
import {
  applyProposalFixtureAssets,
  PROPOSAL_TEMPLATE_SAMPLE_DATA,
  PROPOSAL_TEMPLATE_SOURCE_PINS,
} from "./ProposalTemplateFixtures";
import { crystallizeWithLLM } from "./VizDslCrystallizer";
import { exportDocDslToDocx } from "./DocHelper";
import BlockTree, { ConfigPanel } from "./BlockEditor.jsx";

const WIDTH = 1920;
const HEIGHT = Math.round(WIDTH * 9 / 16);  // 1080

const MODEL_KEY = "viz_dsl_model";

// Test fixtures key their raw data by DSL pin name (supplier / financials /
// sustainability_overview / sustainability_details / facilities), so the
// crystallizer's identity lookup resolves naturally — we only need to declare
// what the `_default` sentinel maps to. `_default` holds the chrome.header
// chips (sector, hq listing, ownership) and supplier_name — pieces that
// don't get an explicit pin in the DSL. In the live flow, this comes from
// `default_input_pin` on the parent query primitive.
const UI_PIN_SOURCE_MAP = {
  _default: "supplier"
};

function loadStored(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}
function storeVal(key, val) {
  try { localStorage.setItem(key, val); } catch { /* noop */ }
}

// Collect a block's own id plus every descendant block's id (any depth).
// Containers (flow/band/conditional) have no leaf shapes of their own — their
// children tag the leaves with the CHILD id (inner-wins guard in _tagBlockShapes)
// — so to resolve a container we union all its descendants' shapes. Leaf
// blocks resolve to a single-element set, so their behaviour is unchanged.
function collectSubtreeBlockIds(dsl, blockId) {
  const CHILD_KEYS = ['flow', 'children', 'blocks', 'then', 'branches', 'bands', 'columns'];
  const find = (node) => {
    if (!node || typeof node !== 'object') return null;
    if (Array.isArray(node)) { for (const c of node) { const r = find(c); if (r) return r; } return null; }
    if (node.id === blockId) return node;
    for (const k of CHILD_KEYS) { if (node[k] !== undefined) { const r = find(node[k]); if (r) return r; } }
    return null;
  };
  const target = find(dsl);
  const ids = new Set();
  if (target) {
    const collect = (n) => {
      if (!n || typeof n !== 'object') return;
      if (Array.isArray(n)) { n.forEach(collect); return; }
      if (n.id) ids.add(n.id);
      for (const k of CHILD_KEYS) { if (n[k] !== undefined) collect(n[k]); }
    };
    collect(target);
  }
  if (ids.size === 0) ids.add(blockId); // safety net
  return ids;
}

// Union bounding box (in canvas pixels) of every shape tagged with any of the
// given `_blockId`s. Used to (a) draw the selection highlight and (b) decide
// which side of the slide a block sits on so the floating config panel anchors
// on the opposite side. Pass a single-element set for a leaf block, or the
// full descendant set for a container. Returns null if nothing is rendered
// (e.g. a block inside an inactive conditional branch).
function getUnionCanvasRect(stage, idSet) {
  if (!stage || !idSet || idSet.size === 0) return null;
  const sx = stage.scaleX() || 1, sy = stage.scaleY() || 1;
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity, found = false;
  const collect = (node) => {
    if (node.getAttr) {
      const bid = node.getAttr('_blockId');
      if (bid && idSet.has(bid)) {
        found = true;
        const abs = node.getAbsolutePosition();
        const cx = abs.x / sx, cy = abs.y / sy;
        const w = (node.width() || 0) * Math.abs(node.scaleX() || 1);
        const h = (node.height() || 0) * Math.abs(node.scaleY() || 1);
        if (cx < x1) x1 = cx;
        if (cy < y1) y1 = cy;
        if (cx + w > x2) x2 = cx + w;
        if (cy + h > y2) y2 = cy + h;
      }
    }
    if (node.getChildren) {
      for (const c of node.getChildren()) collect(c);
    }
  };
  for (const layer of stage.getChildren()) {
    if (layer.name && layer.name() === 'selectionOverlay') continue;
    for (const child of layer.getChildren()) collect(child);
  }
  return found ? { x1, y1, x2, y2 } : null;
}

export default function VizDslTestPage() {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  // Selection overlay layer (persistent across re-renders)
  const selectionOverlayRef = useRef(null);
  // Tracks the natural pixel dimensions of whatever is currently rendered:
  // {w: 1920, h: 1080} for slides, {w: pageWidth, h: totalStackHeight} for docs.
  // fitStage scales to fit these into the available container area.
  const contentDimsRef = useRef({ w: WIDTH, h: HEIGHT });
  const [selectedExample, setSelectedExample] = useState(0);
  const [proposalTemplates, setProposalTemplates] = useState([]);
  const [proposalVisualProfile, setProposalVisualProfile] = useState("core");
  const [proposalTemplatesLoading, setProposalTemplatesLoading] = useState(true);
  const [proposalTemplatesError, setProposalTemplatesError] = useState(null);
  const [dslJson, setDslJson] = useState("");
  const dslJsonRef = useRef("");
  const parsedDsl = useMemo(() => { try { return JSON.parse(dslJson); } catch { return {}; } }, [dslJson]);
  const [dataJson, setDataJson] = useState("");
  const [error, setError] = useState(null);
  const [debug, setDebug] = useState(false);

  // ── mode: "cached" | "llm" ──
  const [mode, setMode] = useState("cached");
  const [crystallizing, setCrystallizing] = useState(false);
  const [llmStatus, setLlmStatus] = useState("");

  // ── source pins (editable in LLM mode) ──
  const [sourcePins, setSourcePins] = useState({});
  const [sourcePinKeys, setSourcePinKeys] = useState([]);

  // ── raw LLM output ──
  const [rawLlmOutput, setRawLlmOutput] = useState("");
  // ── per-pin system prompts + responses for inspection ──
  const [llmCalls, setLlmCalls] = useState([]);
  const [selectedCallIdx, setSelectedCallIdx] = useState(0);
  // ── fullscreen inspector popover: { title, content } | null ──
  const [popover, setPopover] = useState(null);
  // ── canvas click → block selection ──
  const [clickedBlockId, setClickedBlockId] = useState(null);
  const clickedBlockIdRef = useRef(null); // ref copy to avoid stale closures in render callbacks
  // Which side the floating config panel anchors to — "left" or "right".
  // Chosen on selection so the panel avoids covering the clicked block.
  const [panelSide, setPanelSide] = useState("right");
  // Left panel active tab: "structure" (block hierarchy) or "content"
  // (example picker + DSL + data + crystallize). Collapses what used to be
  // two side-by-side panels into one tabbed panel to reclaim canvas width.
  const [leftTab, setLeftTab] = useState("structure");
  // Decide panel side from the block's rendered position: if its center is in
  // the right half of the slide/page, anchor the panel LEFT (and vice versa).
  // Falls back to "right" if the block isn't rendered yet. Refs-only, so the
  // first-render instance captured by the mount-effect click handler stays valid.
  const computePanelSide = (blockId) => {
    const ids = collectSubtreeBlockIds(parsedDsl, blockId);
    const rect = getUnionCanvasRect(stageRef.current, ids);
    if (!rect) return "right";
    const centerX = (rect.x1 + rect.x2) / 2;
    const halfW = (contentDimsRef.current.w || WIDTH) / 2;
    return centerX > halfW ? "left" : "right";
  };
  // ── DSL generation chat ──
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [genError, setGenError] = useState(null);

  // ── provenance (cited fields → evidence quote + verified) ──
  const [provenance, setProvenance] = useState({});
  const provenanceRef = useRef({}); // latest, for the stage hover handler
  const tooltipRef = useRef(null);

  // ── model name (sent to executeAI as `engine`) ──
  const [model, setModel] = useState(loadStored(MODEL_KEY, "openrouter:openai/gpt-oss-120b"));

  // ── document pagination (only meaningful for kind:"document" DSLs) ──
  const [docPageCount, setDocPageCount] = useState(0); // 0 ⇒ current render is not a document
  const [docPageIndex, setDocPageIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  // DOCX bisection: component categories to omit from the export (to isolate what Word rejects)
  const [skipTypes, setSkipTypes] = useState([]);
  const docPageIndexRef = useRef(0);   // latest, for render closures
  const docGroupRef = useRef(null);    // the rendered doc Konva.Group (holds all pages + _docPages attr)
  const docLayoutRef = useRef(null);   // { pageW, pageH, gap, pageCount }

  const proposalExamples = useMemo(() => proposalTemplates.map((template) => ({
    name: `Proposal: ${template.label}`,
    dsl: applyProposalFixtureAssets(template.dsl, template.template_id),
    data: PROPOSAL_TEMPLATE_SAMPLE_DATA[template.template_id] || null,
    sourcePins: PROPOSAL_TEMPLATE_SOURCE_PINS,
    templateMeta: template,
  })), [proposalTemplates]);
  const allExamples = useMemo(() => [...EXAMPLES, ...proposalExamples], [proposalExamples]);
  const currentExample = useMemo(
    () => allExamples[selectedExample] || {},
    [allExamples, selectedExample]
  );

  useEffect(() => {
    dslJsonRef.current = dslJson;
  }, [dslJson]);

  useEffect(() => {
    let cancelled = false;
    async function loadProposalTemplates() {
      try {
        setProposalTemplatesLoading(true);
        setProposalTemplatesError(null);
        const response = await fetch(
          `/api/proposal_slide_templates?visualProfile=${encodeURIComponent(proposalVisualProfile)}`,
          { cache: "no-store" }
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!payload.success || !Array.isArray(payload.templates)) {
          throw new Error(payload.error || 'Invalid template catalogue response');
        }
        if (!cancelled) {
          setProposalTemplates(payload.templates);

          // Changing profile must replace the live DSL as well as the catalogue.
          // Otherwise the selector label updates while the canvas keeps the
          // previously loaded template theme.
          let activeTemplateId = null;
          try {
            activeTemplateId = JSON.parse(dslJsonRef.current)?.presentation?.template_id || null;
          } catch (_) {
            activeTemplateId = null;
          }
          if (activeTemplateId) {
            const templateIndex = payload.templates.findIndex(
              (template) => template.template_id === activeTemplateId
            );
            if (templateIndex >= 0) {
              const nextTemplate = payload.templates[templateIndex];
              setSelectedExample(EXAMPLES.length + templateIndex);
              setDslJson(JSON.stringify(
                applyProposalFixtureAssets(nextTemplate.dsl, nextTemplate.template_id),
                null,
                2
              ));
            }
          }
        }
      } catch (loadError) {
        if (!cancelled) setProposalTemplatesError(loadError.message);
      } finally {
        if (!cancelled) setProposalTemplatesLoading(false);
      }
    }
    loadProposalTemplates();
    return () => { cancelled = true; };
  }, [proposalVisualProfile]);

  // ── Block hit-test by bounding-box containment ────────────────────────
  // When getIntersection misses (e.g. clicking white-space in a block),
  // fall back to computing every block's union rect and testing containment.
  const findBlockAtPosition = (stage, canvasX, canvasY) => {
    if (!stage) return null;
    const sx = stage.scaleX() || 1, sy = stage.scaleY() || 1;
    // Collect shapes grouped by _blockId
    const blocks = {};
    const collect = (node) => {
      const id = node.getAttr && node.getAttr('_blockId');
      if (id) {
        if (!blocks[id]) blocks[id] = [];
        blocks[id].push(node);
      }
      if (node.getChildren) {
        for (const c of node.getChildren()) collect(c);
      }
    };
    for (const layer of stage.getChildren()) {
      if (layer.name && layer.name() === 'selectionOverlay') continue;
      for (const child of layer.getChildren()) collect(child);
    }
    // Compute each block's canvas-space bounding box and test containment.
    // Prefer the smallest block that contains the point (most specific).
    let best = null, bestArea = Infinity;
    for (const [id, shapes] of Object.entries(blocks)) {
      let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
      for (const s of shapes) {
        const abs = s.getAbsolutePosition();
        const cx = abs.x / sx, cy = abs.y / sy;
        const w = (s.width() || 0) * Math.abs(s.scaleX() || 1);
        const h = (s.height() || 0) * Math.abs(s.scaleY() || 1);
        if (cx < x1) x1 = cx; if (cy < y1) y1 = cy;
        if (cx + w > x2) x2 = cx + w; if (cy + h > y2) y2 = cy + h;
      }
      if (canvasX >= x1 && canvasX <= x2 && canvasY >= y1 && canvasY <= y2) {
        const area = (x2 - x1) * (y2 - y1);
        if (area < bestArea) { best = id; bestArea = area; }
      }
    }
    return best;
  };

  // ── Selection overlay helper ──────────────────────────────────────────
  // Resolve the selected block's union rect and draw a highlight overlay.
  // For a LEAF this is just its own shapes; for a CONTAINER (flow/band/
  // conditional) it's the union of every descendant block's shapes, so the
  // whole group highlights (the container has no shapes of its own).
  const updateSelectionOverlay = (overlay, stage, blockId) => {
    if (!overlay || !stage) return;

    const highlight = overlay._selectionHighlight || overlay.findOne('Rect');
    const label = overlay._selectionLabel || overlay.findOne('Text');

    // Clear overlay when no block is selected
    if (!blockId) {
      if (highlight) highlight.visible(false);
      if (label) label.visible(false);
      overlay.batchDraw();
      return;
    }

    // Resolve the selected block + all descendant ids, then collect every
    // shape tagged with any of them. Leaves resolve to {blockId} (unchanged).
    const idSet = collectSubtreeBlockIds(parsedDsl, blockId);
    const shapes = [];
    const collect = (node) => {
      if (node.getAttr) {
        const bid = node.getAttr('_blockId');
        if (bid && idSet.has(bid)) shapes.push(node);
      }
      if (node.getChildren) {
        for (const child of node.getChildren()) collect(child);
      }
    };
    for (const layer of stage.getChildren()) {
      if (layer.name && layer.name() === 'selectionOverlay') continue;
      for (const child of layer.getChildren()) collect(child);
    }

    if (shapes.length === 0) {
      if (highlight) highlight.visible(false);
      if (label) label.visible(false);
      overlay.batchDraw();
      return;
    }

    // getAbsolutePosition includes stage.scale() — divide pos back to canvas px.
    // Width/height are already canvas pixels.
    const sx = stage.scaleX() || 1, sy = stage.scaleY() || 1;
    let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
    for (const s of shapes) {
      const pos = s.getAbsolutePosition();
      const cx = pos.x / sx, cy = pos.y / sy;
      const w = (s.width() || 0) * Math.abs(s.scaleX() || 1);
      const h = (s.height() || 0) * Math.abs(s.scaleY() || 1);
      if (cx < x1) x1 = cx;
      if (cy < y1) y1 = cy;
      if (cx + w > x2) x2 = cx + w;
      if (cy + h > y2) y2 = cy + h;
    }

    if (highlight) {
      highlight.x(x1 - 4);
      highlight.y(y1 - 4);
      highlight.width(x2 - x1 + 8);
      highlight.height(y2 - y1 + 8);
      highlight.fill('rgba(186,117,23,0.2)');
      highlight.stroke('#BA7517');
      highlight.strokeWidth(2);
      highlight.strokeScaleEnabled(false);
      highlight.cornerRadius(4);
      highlight.visible(true);
      highlight.moveToTop();
    }

    if (label) {
      label.text('\u2192 ' + blockId);
      label.x(x1 - 4);
      label.y(y1 - 4 - 20);
      label.fontSize(11);
      label.fontFamily('sans-serif');
      label.fill('#BA7517');
      label.fontStyle('bold');
      label.visible(true);
      label.moveToTop();
    }

    overlay.batchDraw();
  };

  // ── Helpers ────────────────────────────────────────────────────────────
  // Walk a block tree to find a node by its `id` property.
  function findBlockById(node, targetId) {
    if (!node || typeof node !== 'object') return null;
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        const r = findBlockById(node[i], targetId);
        if (r) return r;
      }
      return null;
    }
    if (node.id === targetId) return node;
    const childKeys = ['flow', 'children', 'blocks', 'then', 'branches', 'bands', 'columns'];
    for (let ci = 0; ci < childKeys.length; ci++) {
      const k = childKeys[ci];
      if (node[k] !== undefined) {
        const r = findBlockById(node[k], targetId);
        if (r) return r;
      }
    }
    return null;
  }

  // Surgically replace the block with matching `id` in the tree.
  // Mutates `node` in place. Returns true if a replacement was made.
  function replaceBlockById(node, targetId, replacement) {
    if (!node || typeof node !== 'object' || !targetId) return false;
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        if (node[i] && typeof node[i] === 'object' && node[i].id === targetId) {
          node[i] = replacement;
          return true;
        }
        if (replaceBlockById(node[i], targetId, replacement)) return true;
      }
      return false;
    }
    const childKeys = ['flow', 'children', 'blocks', 'then', 'branches', 'bands', 'columns'];
    for (let ci = 0; ci < childKeys.length; ci++) {
      const k = childKeys[ci];
      if (node[k] !== undefined) {
        if (replaceBlockById(node[k], targetId, replacement)) return true;
      }
    }
    return false;
  }

  // Walk a block tree and collect every data key referenced via `extract`,
  // `key`, or `formula` fields. Returns a Set of field names.
  // Only recurses through known container keys to avoid false positives.
  function collectDataKeysFromBlock(block) {
    if (!block || typeof block !== 'object') return new Set();
    const keys = new Set();
    const containerKeys = ['children','slots','tiles','items','series','branches','then','blocks','flow','bands','columns','value','label','headline','evidence','tag','date','status','qualifier','when','item_template','data_binding','y_axis'];
    const walk = (node) => {
      if (!node || typeof node !== 'object') return;
      // tile_row tile value extracts
      if (node.extract && typeof node.extract === 'string') keys.add(node.extract);
      // Slot / chip keys
      if (node.key && typeof node.key === 'string') keys.add(node.key);
      // Formula variables (e.g. "net_profit/revenue*100" → net_profit, revenue)
      if (node.formula && typeof node.formula === 'string') {
        const vars = node.formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];
        vars.forEach(v => { if (v !== 'a' && v !== 'b') keys.add(v); });
      }
      // Recurse through known container keys
      for (const ck of containerKeys) {
        const child = node[ck];
        if (child !== undefined) {
          if (Array.isArray(child)) child.forEach(walk);
          else if (typeof child === 'object') walk(child);
        }
      }
    };
    walk(block);
    return keys;
  }

  // ── DSL generation ────────────────────────────────────────────────────
  const handleGenerateDSL = async () => {
    const prompt = chatInput.trim();
    if (!prompt || generating) return;
    setGenerating(true);
    setGenError(null);
    setChatInput("");

    // Prepend selection context to the user message
    const selectionContext = clickedBlockId ? ' [selected block: ' + clickedBlockId + '] ' : ' ';
    const userMsg = { role: "user", text: prompt, selection: clickedBlockId };
    setChatMessages(prev => [...prev, userMsg]);
    // Show selection info in the chat
    if (clickedBlockId) {
      setChatMessages(prev => [...prev, {
        role: "assistant",
        text: "Modifying selected block \"" + clickedBlockId + "\" — only this block will change.",
        selection: clickedBlockId
      }]);
    }

    try {
      const body = { prompt };
      // Send current DSL context if modifying
      if (currentExample.dsl) {
        let currentDsl;
        try { currentDsl = JSON.parse(dslJson); } catch { currentDsl = currentExample.dsl; }
        body.currentDSL = currentDsl;
      }
      // Send available pins: merge DSL-declared pins with source pin names so the
      // generator always knows what data is available, even from a blank DSL.
      try {
        const parsed = JSON.parse(dslJson);
        const dslPins = (parsed.pins || []).map(p => typeof p === 'string' ? p : (p.name || p)).filter(Boolean);
        const allPins = [...new Set([...dslPins, ...sourcePinKeys])];
        if (allPins.length > 0) body.pins = allPins;
      } catch {
        if (sourcePinKeys.length > 0) body.pins = [...sourcePinKeys];
      }
      // Send selected block ID if a block is clicked — tells the LLM to scope changes to that block
      if (clickedBlockId) body.selectedBlockId = clickedBlockId;
      if (model) body.model = model;

      const resp = await fetch("/api/generate_dsl", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const json = await resp.json();
      if (!json.success) throw new Error(json.error || "Generation failed");

      const newDsl = json.dsl;
      const debugInfo = json.debug || [];
      // Log full debug to browser console
      console.group('[generate_dsl] Generated DSL');
      console.log('DSL keys:', Object.keys(newDsl || {}));
      console.log('DSL JSON:', JSON.stringify(newDsl, null, 2));
      if (json.brief) {
        console.group('pass1_brief (parsed)');
        console.log('headline:', json.brief.headline);
        console.log('layout_pattern:', json.brief.layout_pattern);
        console.log('visual_tone:', json.brief.visual_tone);
        console.log('accent_color:', json.brief.accent_color);
        console.log('layout_sketch:', json.brief.layout_sketch);
        console.log('content_budget:', json.brief.content_budget);
        console.log('design_rationale:', json.brief.design_rationale);
        console.log('full brief:', json.brief);
        console.groupEnd();
      }
      if (debugInfo && debugInfo.length > 0) {
        debugInfo.forEach(function(d) {
          console.group(d.label + ' (' + (d.chars || d.text.length) + ' chars)');
          console.log(d.text);
          console.groupEnd();
        });
      }
      console.groupEnd();
      // If only a specific block was modified, clear only that block's
      // crystallized data keys instead of the entire data set.
      if (clickedBlockId && dataJson) {
        try {
          const oldDsl = JSON.parse(dslJson);
          const oldBlock = findBlockById(oldDsl, clickedBlockId);
          if (oldBlock) {
            const staleKeys = collectDataKeysFromBlock(oldBlock);
            const oldData = JSON.parse(dataJson);
            const filtered = {};
            for (const [k, v] of Object.entries(oldData)) {
              if (!staleKeys.has(k)) filtered[k] = v;
            }
            // Also clear header_chips sub-keys scoped to the stale keys
            if (oldData.header_chips && typeof oldData.header_chips === 'object') {
              const hc = {};
              for (const [k, v] of Object.entries(oldData.header_chips)) {
                if (!staleKeys.has(k)) hc[k] = v;
              }
              filtered.header_chips = Object.keys(hc).length > 0 ? hc : undefined;
            }
            setDataJson(Object.keys(filtered).length > 0
              ? JSON.stringify(filtered, null, 2)
              : "");
          } else {
            // Block not found in old DSL (e.g. newly created) — clear all
            setDataJson("");
          }
        } catch (_) {
          // If anything fails, fall back to clearing all data
          setDataJson("");
        }
      } else {
        setDataJson("");
      }
      // When a block was selected, surgically replace only that block
      // in the current DSL. This handles both cases: (a) the LLM returns
      // the full DSL with the block updated, or (b) the LLM returns just
      // the modified block subtree.
      if (clickedBlockId) {
        let currentDsl;
        try { currentDsl = JSON.parse(dslJson); } catch { currentDsl = null; }
        if (currentDsl && typeof currentDsl === 'object') {
          // If newDsl is a complete DSL (has version/kind at root),
          // extract the modified block from it. Otherwise treat newDsl
          // as the replacement block directly.
          let replacementBlock;
          if (newDsl.version != null || newDsl.kind != null) {
            replacementBlock = findBlockById(newDsl, clickedBlockId);
          }
          if (!replacementBlock) {
            replacementBlock = newDsl;
          }
          const replaced = replaceBlockById(currentDsl, clickedBlockId, replacementBlock);
          if (replaced) {
            setDslJson(JSON.stringify(currentDsl, null, 2));
          } else {
            // Block not found in current DSL (e.g. it was added) — use full result
            setDslJson(JSON.stringify(newDsl, null, 2));
          }
        } else {
          setDslJson(JSON.stringify(newDsl, null, 2));
        }
      } else {
        setDslJson(JSON.stringify(newDsl, null, 2));
      }
      const wasSelected = clickedBlockId;
      setClickedBlockId(null);
      setError(null);
      const selectedLabel = wasSelected ? ' (modified block: ' + wasSelected + ')' : '';
      setChatMessages(prev => [...prev, {
        role: "assistant",
        text: "DSL generated" + selectedLabel + " — " + (debugInfo.length > 0 ? debugInfo.map(function(d){return d.label}).join(', ') + ' ✓' : 'done') + ". Crystallize or paste data to render.",
        dsl: newDsl
      }]);
    } catch (e) {
      setGenError(e.message);
      setChatMessages(prev => [...prev, { role: "assistant", text: "Error: " + e.message, error: true }]);
    } finally {
      setGenerating(false);
    }
  };



  const imageCallback = (node) => {
    if (stageRef.current) {
      if (node && node.refreshCache) node.refreshCache();
      stageRef.current.batchDraw();
    }
  };

  // Render a multi-page document into a single Konva.Group, with pages stacked
  // vertically. The pagination controls then show one page at a time by
  // translating this group and sizing the stage to a single page (see
  // applyDocPage) — so all pages stay live for export without re-rendering.
  const buildDocGroup = (dsl, data) => {
    const docResult = renderDocDslDocument(dsl, data, {
      width: dsl.page?.width,
      imageCallback,
      debug,
      provenance: provenanceRef.current
    });
    const pages = docResult.pages || [];
    const pageW = dsl.page?.width || 816;
    const pageH = dsl.page?.height || 1056;
    const gap = 24;

    const group = new Konva.Group();
    pages.forEach((p, i) => {
      p.y(i * (pageH + gap));
      group.add(p);
    });
    group.setAttr("_docPages", pages);
    group.setAttr("_dsl", dsl);

    // Stash for the page navigator + export (contentDims is set per-page in applyDocPage)
    docGroupRef.current = group;
    docLayoutRef.current = { pageW, pageH, gap, pageCount: pages.length };
    return group;
  };

  const fitStage = useCallback(() => {
    if (!containerRef.current || !stageRef.current) return;
    const parent = containerRef.current.parentElement;
    if (!parent) return;
    const { w, h } = contentDimsRef.current;
    const availW = parent.clientWidth - 32;
    const availH = parent.clientHeight - 32;
    const scale = Math.min(availW / w, availH / h, 1);
    stageRef.current.width(w * scale);
    stageRef.current.height(h * scale);
    stageRef.current.scale({ x: scale, y: scale });
    stageRef.current.batchDraw();
  }, []);

  // Show a single document page: translate the stacked group so the target page
  // sits at y=0 and size the stage to one page (the container clips the rest).
  const applyDocPage = useCallback((idx) => {
    const layout = docLayoutRef.current;
    const group = docGroupRef.current;
    if (!layout || !group || !stageRef.current) return;
    const clamped = Math.max(0, Math.min(idx, layout.pageCount - 1));
    group.y(-clamped * (layout.pageH + layout.gap));
    contentDimsRef.current = { w: layout.pageW, h: layout.pageH };
    fitStage();
    stageRef.current.batchDraw();
  }, [fitStage]);

  // After a fresh render, sync pagination state to whatever was just drawn.
  const finishMount = useCallback((dsl) => {
    if (dsl?.kind === "document") {
      const count = docLayoutRef.current?.pageCount || 0;
      setDocPageCount(count);
      const idx = Math.max(0, Math.min(docPageIndexRef.current, count - 1));
      docPageIndexRef.current = idx;
      setDocPageIndex(idx);
      applyDocPage(idx);
    } else {
      setDocPageCount(0);
      docGroupRef.current = null;
      docLayoutRef.current = null;
      fitStage();
    }
  }, [applyDocPage, fitStage]);

  const goToPage = useCallback((idx) => {
    const count = docLayoutRef.current?.pageCount || 0;
    const clamped = Math.max(0, Math.min(idx, count - 1));
    docPageIndexRef.current = clamped;
    setDocPageIndex(clamped);
    applyDocPage(clamped);
  }, [applyDocPage]);

  // Export the currently-rendered document to DOCX via the shared doc exporter.
  // Reuses the live on-screen page groups so embedded images are already loaded.
  const handleExportDocx = useCallback(async () => {
    try {
      setError(null);
      const dsl = JSON.parse(dslJson);
      if (dsl.kind !== "document") {
        setError("Export to DOCX is only available for document DSLs.");
        return;
      }
      const pages = docGroupRef.current?.getAttr("_docPages");
      if (!pages || pages.length === 0) {
        setError("Render the document before exporting.");
        return;
      }
      setExporting(true);
      const base = currentExample?.name ? currentExample.name.replace(/\s+/g, "_") : (dsl.title || "document");
      await exportDocDslToDocx(pages, dsl, { filename: `${base}.docx`, skip: skipTypes });
    } catch (e) {
      setError(e.message + '\n' + (e.stack || ''));
    } finally {
      setExporting(false);
    }
  }, [dslJson, currentExample, skipTypes]);

  const loadExample = (idx) => {
    const ex = allExamples[idx];
    if (!ex) return;
    setSelectedExample(idx);
    setDslJson(JSON.stringify(ex.dsl, null, 2));
    if (ex.sourcePins) {
      setSourcePins({ ...ex.sourcePins });
      setSourcePinKeys(Object.keys(ex.sourcePins));
      // In LLM mode, start with empty data so user must crystallize
      if (mode === "llm") {
        setDataJson("");
      } else if (ex.data) {
        setDataJson(JSON.stringify(ex.data, null, 2));
      } else {
        setDataJson("");
      }
    } else if (ex.data) {
      setDataJson(JSON.stringify(ex.data, null, 2));
      setSourcePins({});
      setSourcePinKeys([]);
    } else {
      setDataJson("");
      setSourcePins({});
      setSourcePinKeys([]);
    }
    setClickedBlockId(null);
    setError(null);
    setLlmStatus("");
    setRawLlmOutput("");
    setLlmCalls([]);
    setSelectedCallIdx(0);
    provenanceRef.current = {};
    setProvenance({});
    docPageIndexRef.current = 0;
    setDocPageIndex(0);
    setDocPageCount(0);
    docGroupRef.current = null;
    docLayoutRef.current = null;
  };

  const renderSlide = useCallback(() => {
    try {
      setError(null);
      if (!stageRef.current) return;

      try { JSON.parse(dslJson); } catch (e) { return; }

      const dsl = JSON.parse(dslJson);
      let data = {};
      let isPreview = false;
      if (dataJson) {
        try { data = JSON.parse(dataJson); } catch (e) { /* use empty data */ }
      } else {
        isPreview = true;
      }

      stageRef.current.destroyChildren();
      const layer = new Konva.Layer();

      // Capture Konva draw errors with full stack
      const origDraw = layer.draw.bind(layer);
      layer.draw = function() {
        try {
          return origDraw();
        } catch (drawErr) {
          console.error('[Konva draw error]', drawErr);
          setError(drawErr.message + '\n' + (drawErr.stack || ''));
          throw drawErr;
        }
      };

      let group;
      if (dsl.kind === "document") {
        group = buildDocGroup(dsl, data);
      } else {
        contentDimsRef.current = { w: WIDTH, h: HEIGHT };
        group = renderVizDslSlide(dsl, data, {
          width: WIDTH,
          imageCallback,
          debug,
          provenance: provenanceRef.current,
          sourcePins
        });
      }
      layer.add(group);
      stageRef.current.add(layer);
      // (Re)create and add selection overlay layer — persists across re-renders
      // Non-interactive shapes so clicks pass through to the content layer
      const overlayLayer = new Konva.Layer({ name: 'selectionOverlay', listening: false });
      const hl = new Konva.Rect({ name: 'selectionHighlight', visible: false, listening: false });
      const lbl = new Konva.Text({ name: 'selectionLabel', visible: false, listening: false });
      overlayLayer._selectionHighlight = hl;
      overlayLayer._selectionLabel = lbl;
      overlayLayer.add(hl);
      overlayLayer.add(lbl);
      stageRef.current.add(overlayLayer);
      selectionOverlayRef.current = overlayLayer;
      // Refresh overlay for current selection (use ref to avoid stale closure)
      updateSelectionOverlay(overlayLayer, stageRef.current, clickedBlockIdRef.current);
      finishMount(dsl);
    } catch (e) {
      console.error('[render error]', e);
      setError(e.message + '\n' + (e.stack || ''));
    }
  }, [dslJson, dataJson, fitStage, finishMount, debug, sourcePins]);

  // ── LLM crystallization ──
  const handleCrystallize = async () => {
    setError(null);
    setLlmStatus("");
    setCrystallizing(true);
    try {
      const dsl = JSON.parse(dslJson);
      storeVal(MODEL_KEY, model);
      const result = await crystallizeWithLLM(dsl, sourcePins, {
        model,
        pinSourceMap: UI_PIN_SOURCE_MAP,
        onProgress: (msg) => setLlmStatus(msg)
      });
      setDataJson(JSON.stringify(result.data, null, 2));
      setRawLlmOutput(result.raw || "");
      setLlmCalls(result.calls || []);
      setSelectedCallIdx(0);
      provenanceRef.current = result.provenance || {};
      setProvenance(result.provenance || {});
      setLlmStatus("Done — click Render to see the slide.");
      // Schedule a render with the new data after state settles
      setTimeout(() => {
        try {
          if (!stageRef.current) return;
          const layer = new Konva.Layer();
          // Trap draw errors
          const origDraw = layer.draw.bind(layer);
          layer.draw = function() {
            try { return origDraw(); } catch (drawErr) {
              console.error('[Konva draw error]', drawErr);
              setError(drawErr.message + '\n' + (drawErr.stack || ''));
              throw drawErr;
            }
          };
          stageRef.current.destroyChildren();
          const dsl = JSON.parse(dslJson);
          // Fresh crystallization → start at the first page
          docPageIndexRef.current = 0;
          setDocPageIndex(0);
          let group;
          if (dsl.kind === "document") {
            group = buildDocGroup(dsl, result.data);
          } else {
            contentDimsRef.current = { w: WIDTH, h: HEIGHT };
            group = renderVizDslSlide(dsl, result.data, {
              width: WIDTH, imageCallback, debug, provenance: provenanceRef.current,
              sourcePins
            });
          }
          layer.add(group);
          stageRef.current.add(layer);
          // (Re)create and add selection overlay layer
          // Non-interactive shapes so clicks pass through to the content layer
          const overlayLayer = new Konva.Layer({ name: 'selectionOverlay', listening: false });
          const hl = new Konva.Rect({ name: 'selectionHighlight', visible: false, listening: false });
          const lbl = new Konva.Text({ name: 'selectionLabel', visible: false, listening: false });
          overlayLayer._selectionHighlight = hl;
          overlayLayer._selectionLabel = lbl;
          overlayLayer.add(hl);
          overlayLayer.add(lbl);
          stageRef.current.add(overlayLayer);
          selectionOverlayRef.current = overlayLayer;
          // Refresh overlay for current selection (use ref to avoid stale closure)
          updateSelectionOverlay(overlayLayer, stageRef.current, clickedBlockIdRef.current);
          finishMount(dsl);
        } catch (renderErr) {
          setError(renderErr.message);
        }
      }, 100);
    } catch (e) {
      setError(e.message);
      setLlmStatus("");
    } finally {
      setCrystallizing(false);
    }
  };

  // ── switch mode ──
  const switchMode = (newMode) => {
    setMode(newMode);
    const ex = allExamples[selectedExample];
    if (!ex) return;
    if (newMode === "llm") {
      if (ex.sourcePins) {
        setSourcePins({ ...ex.sourcePins });
        setSourcePinKeys(Object.keys(ex.sourcePins));
        setDataJson("");
      }
    } else {
      if (ex.data) {
        setDataJson(JSON.stringify(ex.data, null, 2));
      }
    }
    setError(null);
    setLlmStatus("");
  };

  useEffect(() => {
    if (containerRef.current && !stageRef.current) {
      const stage = new Konva.Stage({
        container: containerRef.current,
        width: WIDTH,
        height: HEIGHT
      });
      stageRef.current = stage;
      // Evidence tooltips. Konva's own stage event stream isn't relied on here
      // (it's disabled in the real InfiniteCanvas host, and didn't fire in the
      // playground either). Instead we bind a native DOM listener on the
      // container and hit-test with stage.getIntersection — cited nodes are
      // tagged with _evidence by the renderer.
      const container = containerRef.current;
      const showTip = (shape, clientX, clientY) => {
        const tip = tooltipRef.current;
        if (!tip) return;
        const ev = shape && shape.getAttr && shape.getAttr("_evidence");
        if (!ev) { tip.style.display = "none"; return; }
        const verified = shape.getAttr("_evidenceVerified") !== false;
        tip.innerHTML = "";
        const hdr = document.createElement("div");
        hdr.textContent = verified ? "✓ Verified in source" : "⚠ Not found in source";
        hdr.style.cssText = `font-weight:600;margin-bottom:4px;color:${verified ? "#7FD1A6" : "#F0A0A0"}`;
        const body = document.createElement("div");
        body.textContent = ev;
        tip.appendChild(hdr);
        tip.appendChild(body);
        tip.style.display = "block";
        tip.style.left = (clientX + 14) + "px";
        tip.style.top = (clientY + 14) + "px";
      };
      // Raw DOM coords relative to the Konva content div.  getIntersection
      // internally applies 1/stage.scaleX() to convert to layer coords.
      const getPos = (e) => {
        const st = stageRef.current;
        if (!st) return null;
        const el = st.content || container;
        const rect = el.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };
      const onMove = (e) => {
        const st = stageRef.current;
        if (!st) return;
        const pos = getPos(e);
        if (!pos) return;
        let shape = null;
        try { shape = st.getIntersection(pos); } catch { shape = null; }
        showTip(shape, e.clientX, e.clientY);
      };
      const onLeave = () => { if (tooltipRef.current) tooltipRef.current.style.display = "none"; };
      const onCanvasClick = (e) => {
        const st = stageRef.current;
        if (!st) return;
        const pos = getPos(e);
        if (!pos) return;
        let shape = null;
        try { shape = st.getIntersection(pos); } catch { shape = null; }
        let blockId = shape && shape.getAttr && shape.getAttr("_blockId");
        // Fallback: if no shape was hit (or hit a shape without _blockId,
        // e.g. slide background / whitespace), test every block's bounding
        // box via math containment.  Convert DOM coords to canvas px first.
        if (!blockId) {
          const canvasX = pos.x / (st.scaleX() || 1);
          const canvasY = pos.y / (st.scaleY() || 1);
          const found = findBlockAtPosition(st, canvasX, canvasY);
          if (found) blockId = found;
        }
        // Update overlay — use hit shape directly if available (fast path),
        // otherwise fall back to union-based updateSelectionOverlay.
        if (blockId) {
          if (shape && shape.getAttr && shape.getAttr("_blockId") === blockId) {
            const ol = selectionOverlayRef.current;
            if (ol) {
              const hl = ol._selectionHighlight;
              const lbl = ol._selectionLabel;
              const abs = shape.getAbsolutePosition();
              const sx = st.scaleX() || 1, sy = st.scaleY() || 1;
              const cx = abs.x / sx, cy = abs.y / sy;
              const w = (shape.width() || 0) * Math.abs(shape.scaleX() || 1);
              const h = (shape.height() || 0) * Math.abs(shape.scaleY() || 1);
              if (hl) {
                hl.x(cx - 4); hl.y(cy - 4);
                hl.width(w + 8); hl.height(h + 8);
                hl.fill('rgba(186,117,23,0.2)');
                hl.stroke('#BA7517'); hl.strokeWidth(2);
                hl.strokeScaleEnabled(false); hl.cornerRadius(4);
                hl.visible(true); hl.moveToTop();
              }
              if (lbl) {
                lbl.text('\u2192 ' + blockId);
                lbl.x(cx - 4); lbl.y(cy - 4 - 20);
                lbl.fontSize(11); lbl.fontFamily('sans-serif');
                lbl.fill('#BA7517'); lbl.fontStyle('bold');
                lbl.visible(true); lbl.moveToTop();
              }
              ol.batchDraw();
            }
          } else {
            updateSelectionOverlay(selectionOverlayRef.current, st, blockId);
          }
          // Compute next selection from the authoritative ref (set sync),
          // then derive the panel side before the state update flushes.
          const next = clickedBlockIdRef.current === blockId ? null : blockId;
          clickedBlockIdRef.current = next;
          setClickedBlockId(next);
          if (next) setPanelSide(computePanelSide(next));
        } else {
          // Deselect — hide overlay
          const ol = selectionOverlayRef.current;
          if (ol) {
            const hl = ol._selectionHighlight;
            const lbl = ol._selectionLabel;
            if (hl) hl.visible(false);
            if (lbl) lbl.visible(false);
            ol.batchDraw();
          }
          setClickedBlockId(null);
          clickedBlockIdRef.current = null;
        }
      };
      container.addEventListener("mousemove", onMove);
      container.addEventListener("mouseleave", onLeave);
      container.addEventListener("click", onCanvasClick);
    }
    loadExample(0);
    window.addEventListener("resize", fitStage);
    return () => window.removeEventListener("resize", fitStage);
  }, [fitStage]);

  useEffect(() => {
    if (stageRef.current && dslJson) {
      const timer = setTimeout(renderSlide, 50);
      return () => clearTimeout(timer);
    }
  }, [selectedExample, debug, dslJson, dataJson, sourcePins, renderSlide]); // eslint-disable-line

  // The pagination toolbar appearing/disappearing changes the available canvas
  // height — refit the current page once that layout has committed.
  useEffect(() => {
    if (docPageCount > 0) {
      const id = setTimeout(() => applyDocPage(docPageIndexRef.current), 0);
      return () => clearTimeout(id);
    }
  }, [docPageCount, applyDocPage]);

  // Sync clickedBlockIdRef whenever clickedBlockId changes
  useEffect(() => {
    clickedBlockIdRef.current = clickedBlockId;
  }, [clickedBlockId]);

  // ── Selection overlay: highlight the clicked block ──
  useEffect(() => {
    updateSelectionOverlay(selectionOverlayRef.current, stageRef.current, clickedBlockId);
  }, [clickedBlockId]);

  const hasSourcePins = sourcePinKeys.length > 0;
  const showCrystallize = mode === "llm" && hasSourcePins;

  return (
    <div style={{ display: "flex", height: "calc(100vh - 4rem)", fontFamily: "sans-serif" }}>
      {/* ── left sidebar ── */}
      {/* ── left panel (tabbed): Structure / Content ──
          Replaces the former two side-by-side panels (example+DSL+data, and
          the hierarchy tree) with a single tabbed panel. Structure holds the
          block tree (default — the editing surface); Content holds example
          picker, DSL, data, crystallize. */}
      <div style={{ width: 400, display: "flex", flexDirection: "column", borderRight: "1px solid #ddd", background: "#fff", overflow: "hidden" }}>
        {/* Tab bar */}
        <div className="flex shrink-0 border-b border-slate-200 bg-slate-50">
          {[
            { id: "structure", label: "Structure", icon: "lucide:layers" },
            { id: "content", label: "Content", icon: "lucide:database" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setLeftTab(t.id)}
              className={cn(
                "flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold transition-colors",
                leftTab === t.id
                  ? "bg-white text-ccgreen-700 border-b-2 border-ccgreen-600"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-100/60"
              )}
            >
              <Icon icon={t.icon} className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab body — each tab owns its own scrolling. */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {leftTab === "structure" ? (
            <BlockTree
              key={selectedExample}
              dsl={parsedDsl}
              onDSLChange={(newDSL) => setDslJson(JSON.stringify(newDSL, null, 2))}
              selectedId={clickedBlockId}
              onSelect={(id) => {
                // Single source of truth: a tree click highlights the block on
                // the canvas overlay (and opens the floating config panel).
                setClickedBlockId(id);
                if (id) setPanelSide(computePanelSide(id));
              }}
            />
          ) : (
          <div style={{ height: "100%", padding: 12, overflow: "auto", background: "#f5f5f5", display: "flex", flexDirection: "column", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>Viz DSL Test</h3>

        {/* Example selector */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Proposal visual system</label>
          <select
            value={proposalVisualProfile}
            onChange={(e) => setProposalVisualProfile(e.target.value)}
            style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #ccc" }}
          >
            <option value="core">Core Co-Created</option>
            <option value="sense">SENSE</option>
            <option value="open_lab">Open Lab 2026</option>
          </select>
        </div>

        <div>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Example</label>
          <select
            value={selectedExample}
            onChange={(e) => loadExample(Number(e.target.value))}
            style={{ width: "100%", padding: "4px 6px", fontSize: 11, borderRadius: 4, border: "1px solid #ccc" }}
          >
            <optgroup label="Existing examples">
              {EXAMPLES.map((ex, i) => (
                <option key={i} value={i}>{ex.name}</option>
              ))}
            </optgroup>
            <optgroup label={proposalTemplatesLoading ? "Proposal templates (loading...)" : "Proposal templates"}>
              {proposalExamples.map((ex, i) => {
                const idx = EXAMPLES.length + i;
                return <option key={ex.templateMeta.template_id} value={idx}>{ex.name}</option>;
              })}
            </optgroup>
          </select>
          {proposalTemplatesError && (
            <div style={{ marginTop: 4, color: "#b42318", fontSize: 10 }}>
              Proposal templates unavailable: {proposalTemplatesError}
            </div>
          )}
        </div>

        {currentExample.templateMeta && (
          <div style={{ padding: "7px 8px", background: "#eef2e8", border: "1px solid #d7dfce", borderRadius: 4, fontSize: 10, lineHeight: 1.35 }}>
            <div style={{ fontWeight: 700, color: "#1F3A2E" }}>{currentExample.templateMeta.template_id}</div>
            <div><strong>Visual system:</strong> {parsedDsl.presentation?.visual_profile || currentExample.templateMeta.visual_profile}</div>
            <div style={{ marginTop: 3, color: "#475467" }}>{currentExample.templateMeta.description}</div>
            <div style={{ marginTop: 5 }}><strong>Sections:</strong> {currentExample.templateMeta.section_ids.join(', ')}</div>
            <div><strong>Page types:</strong> {currentExample.templateMeta.page_types.join(', ')}</div>
            <div><strong>Visual intents:</strong> {currentExample.templateMeta.visual_intents.join(', ')}</div>
            <div><strong>Assets:</strong> {currentExample.templateMeta.requires_assets.length ? currentExample.templateMeta.requires_assets.join(', ') : 'none'}</div>
          </div>
        )}

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 4 }}>
          <button
            onClick={() => switchMode("cached")}
            style={{
              flex: 1, padding: "4px 0", fontSize: 11, borderRadius: 4, border: "1px solid #ccc",
              background: mode === "cached" ? "#1F3A2E" : "#fff",
              color: mode === "cached" ? "#fff" : "#333",
              cursor: "pointer"
            }}
          >
            Cached Data
          </button>
          <button
            onClick={() => switchMode("llm")}
            style={{
              flex: 1, padding: "4px 0", fontSize: 11, borderRadius: 4, border: "1px solid #ccc",
              background: mode === "llm" ? "#1F3A2E" : "#fff",
              color: mode === "llm" ? "#fff" : "#333",
              cursor: "pointer"
            }}
          >
            LLM Crystallize
          </button>
        </div>

        {/* LLM config (only in LLM mode) */}
        {mode === "llm" && (
          <div style={{ padding: "6px 8px", background: "#eef2e8", borderRadius: 4, fontSize: 11 }}>
            <label style={{ fontWeight: 600, display: "block" }}>Model (engine)</label>
            <input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={{ width: "100%", padding: "3px 5px", fontSize: 11, borderRadius: 3, border: "1px solid #ccc", fontFamily: "monospace" }}
            />
          </div>
        )}

        {/* DSL */}
        <div style={{ flex: "0 0 auto" }}>
          <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>DSL</label>
          <textarea
            style={{ width: "100%", height: 200, fontFamily: "monospace", fontSize: 10, resize: "vertical" }}
            value={dslJson}
            onChange={(e) => setDslJson(e.target.value)}
          />
        </div>

        {/* Crystallize action (LLM mode only) — above source pins so always visible */}
        {showCrystallize && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={handleCrystallize}
              disabled={crystallizing}
              style={{
                padding: "6px 16px", background: crystallizing ? "#888" : "#BA7517", color: "#fff",
                border: "none", borderRadius: 4, cursor: crystallizing ? "not-allowed" : "pointer", fontSize: 12
              }}
            >
              {crystallizing ? "Crystallizing…" : "▶ Crystallize with LLM"}
            </button>
            <label style={{ fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} style={{ marginRight: 4 }} />
              Debug
            </label>
          </div>
        )}

        {/* Status & errors (both modes) */}
        {llmStatus && (
          <div style={{ padding: "4px 8px", background: "#e8f0e8", borderRadius: 4, fontSize: 11, color: "#2D8659" }}>
            {llmStatus}
          </div>
        )}
        {error && (
          <div style={{ padding: 8, background: "#ffe0e0", borderRadius: 4, fontSize: 11, color: "#a00", whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        )}

        {/* Source pins (LLM mode only) */}
        {showCrystallize && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>
            <label style={{ fontSize: 11, fontWeight: 600 }}>Source Pins</label>
            <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {sourcePinKeys.map((key) => (
                <div key={key}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#555", marginBottom: 2 }}>{key}</div>
                  <textarea
                    style={{ width: "100%", height: 100, fontFamily: "monospace", fontSize: 9, resize: "vertical" }}
                    value={typeof sourcePins[key] === "string" ? sourcePins[key] : (sourcePins[key] != null ? JSON.stringify(sourcePins[key], null, 2) : "")}
                    onChange={(e) => {
                      const raw = e.target.value;
                      let parsed = raw;
                      try { parsed = JSON.parse(raw); } catch { /* keep as string */ }
                      setSourcePins((prev) => ({ ...prev, [key]: parsed }));
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Crystalized data (cached mode, or after LLM crystallization) */}
        {!showCrystallize && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Crystalized Data</label>
            <textarea
              style={{ flex: 1, width: "100%", fontFamily: "monospace", fontSize: 10, resize: "vertical" }}
              value={dataJson}
              onChange={(e) => setDataJson(e.target.value)}
            />
          </div>
        )}

        {/* Raw LLM output (after crystallization) */}
        {rawLlmOutput && (
          <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", maxHeight: 180 }}>
            <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>Raw LLM Output</label>
            <textarea
              readOnly
              style={{ width: "100%", height: 140, fontFamily: "monospace", fontSize: 9, resize: "vertical", background: "#fafafa" }}
              value={rawLlmOutput}
            />
          </div>
        )}

        {/* Per-call prompt inspector (after crystallization) */}
        {llmCalls.length > 0 && (
          <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", maxHeight: 360 }}>
            <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>
              LLM Calls ({llmCalls.length}) — system prompt + response per pin
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 4 }}>
              {llmCalls.map((c, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedCallIdx(i)}
                  style={{
                    padding: "3px 8px", fontSize: 10, cursor: "pointer",
                    border: "1px solid " + (i === selectedCallIdx ? "#1F3A2E" : "#ccc"),
                    background: i === selectedCallIdx ? "#1F3A2E" : "#fff",
                    color: i === selectedCallIdx ? "#fff" : "#333",
                    borderRadius: 3
                  }}
                >
                  {i + 1}. {c.sourceKey} <span style={{ opacity: 0.7 }}>({c.dslPin})</span>
                  {c.ok ? "" : " ⚠"}
                </button>
              ))}
            </div>
            {llmCalls[selectedCallIdx] && (() => {
              const call = llmCalls[selectedCallIdx];
              const promptText = call.systemPrompt || "";
              const schemaText = call.schema ? JSON.stringify(call.schema, null, 2) : "(no schema — fell back to json_object mode)";
              const responseText = JSON.stringify(call.response ?? call.raw ?? "", null, 2);
              const cols = [
                { title: `System prompt (${promptText.length} chars)`, content: promptText },
                { title: "JSON Schema (response_format)", content: schemaText },
                { title: `Response${call.error ? ` · error: ${call.error}` : ""}`, content: responseText },
              ];
              return (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {cols.map((col, ci) => (
                    <div key={ci} style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
                        <label style={{ fontSize: 10, color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col.title}</label>
                        <button
                          onClick={() => setPopover({ title: `${col.title} — ${call.sourceKey} (${call.dslPin})`, content: col.content })}
                          title="Open full screen"
                          style={{ flex: "0 0 auto", fontSize: 10, padding: "1px 6px", cursor: "pointer", border: "1px solid #ccc", borderRadius: 3, background: "#fff" }}
                        >
                          ⛶ Expand
                        </button>
                      </div>
                      <textarea
                        readOnly
                        style={{ width: "100%", height: 280, fontFamily: "monospace", fontSize: 9, resize: "vertical", background: "#fafafa" }}
                        value={col.content}
                      />
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Provenance — cited fields with verbatim-source verification */}
        {Object.keys(provenance).length > 0 && (
          <div style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", maxHeight: 220 }}>
            <label style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 2 }}>
              Provenance ({Object.keys(provenance).length} cited · hover the slide to see quotes)
            </label>
            <div style={{ overflow: "auto", border: "1px solid #e0e0e0", borderRadius: 4, background: "#fafafa", padding: 6 }}>
              {Object.entries(provenance).map(([k, p]) => (
                <div key={k} style={{ marginBottom: 6, fontSize: 10 }}>
                  <div style={{ fontWeight: 600 }}>
                    <span style={{ color: p.verified ? "#2D8659" : "#C4463A" }}>{p.verified ? "✓" : "⚠"}</span> {k}
                  </div>
                  <div style={{ color: "#555", fontStyle: "italic" }}>"{p.evidence}"</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions — Render button (cached mode only; crystallize button is above source pins) */}
        {!showCrystallize && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <button
              onClick={renderSlide}
              style={{
                padding: "6px 16px", background: "#1F3A2E", color: "#fff",
                border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12
              }}
            >
              {dataJson ? "Render" : "Preview"}
            </button>
            <label style={{ fontSize: 11, cursor: "pointer" }}>
              <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} style={{ marginRight: 4 }} />
              Debug
            </label>
          </div>
        )}
          </div>
        )}
        </div>
      </div>


      {/* ── canvas ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", background: "#e8e8e8", overflow: "hidden" }}>
        {/* Toolbar: page navigator (documents only) + DOCX export */}
        {docPageCount > 0 && (
          <div key="vizdsl-toolbar" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "#f0f0f0", borderBottom: "1px solid #ddd" }}>
            <button
              onClick={() => goToPage(docPageIndex - 1)}
              disabled={docPageIndex <= 0}
              style={{ padding: "5px 12px", fontSize: 12, borderRadius: 4, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: docPageIndex <= 0 ? "not-allowed" : "pointer", opacity: docPageIndex <= 0 ? 0.5 : 1 }}
            >
              ◀ Prev
            </button>
            <span style={{ fontSize: 12, color: "#333", minWidth: 96, textAlign: "center" }}>
              Page {docPageIndex + 1} / {docPageCount}
            </span>
            <button
              onClick={() => goToPage(docPageIndex + 1)}
              disabled={docPageIndex >= docPageCount - 1}
              style={{ padding: "5px 12px", fontSize: 12, borderRadius: 4, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: docPageIndex >= docPageCount - 1 ? "not-allowed" : "pointer", opacity: docPageIndex >= docPageCount - 1 ? 0.5 : 1 }}
            >
              Next ▶
            </button>
            <div style={{ flex: 1 }} />
            {/* Bisection: tick a component to OMIT it from the DOCX, to isolate what Word rejects */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11, color: "#555" }}>
              <span style={{ fontWeight: 600 }}>Skip:</span>
              {["heading", "paragraph", "listItem", "table", "visual", "image", "header", "footer"].map(cat => (
                <label key={cat} style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 2 }}>
                  <input
                    type="checkbox"
                    checked={skipTypes.includes(cat)}
                    onChange={(e) => setSkipTypes(prev => e.target.checked ? [...prev, cat] : prev.filter(c => c !== cat))}
                  />
                  {cat}
                </label>
              ))}
              {skipTypes.length > 0 && (
                <button onClick={() => setSkipTypes([])} style={{ fontSize: 10, padding: "1px 6px", cursor: "pointer", border: "1px solid #ccc", borderRadius: 3, background: "#fff" }}>
                  reset
                </button>
              )}
            </div>
            <button
              onClick={handleExportDocx}
              disabled={exporting}
              style={{ padding: "5px 16px", fontSize: 12, borderRadius: 4, border: "none", background: exporting ? "#888" : "#1F3A2E", color: "#fff", cursor: exporting ? "not-allowed" : "pointer" }}
            >
              {exporting ? "Exporting…" : "⬇ Export DOCX"}
            </button>
          </div>
        )}
        <div key="vizdsl-canvas" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: 16, position: "relative" }}>
          {dslJson && !dataJson && (
            <div style={{
              position: "absolute", top: 8, right: 12, zIndex: 10,
              background: "rgba(31,58,46,0.85)", color: "#fff",
              padding: "4px 12px", borderRadius: 4,
              fontSize: 11, fontWeight: 600,
              pointerEvents: "none"
            }}>
              PREVIEW — not crystallized
            </div>
          )}
          <div
            ref={containerRef}
            style={{
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
              borderRadius: 4,
              overflow: "hidden"
            }}
          />
          {/* Floating config panel (PowerPoint-style). Anchored to this
              canvas area (position: relative). Keyed by selection so switching
              blocks remounts it (resets collapsible-field state). */}
          {clickedBlockId && (
            <ConfigPanel
              key={clickedBlockId}
              dsl={parsedDsl}
              onDSLChange={(newDSL) => setDslJson(JSON.stringify(newDSL, null, 2))}
              selectedId={clickedBlockId}
              side={panelSide}
              onClose={() => setClickedBlockId(null)}
            />
          )}
        </div>
      </div>

      {/* ── DSL generation chat panel ── */}
      <div style={{
        position: "fixed", bottom: 0, right: 0, zIndex: 500,
        width: chatOpen ? 420 : "auto",
        background: "#fff", borderTop: "1px solid #ddd",
        borderLeft: chatOpen ? "1px solid #ddd" : "none",
        borderTopLeftRadius: 8, boxShadow: chatOpen ? "0 -4px 20px rgba(0,0,0,0.12)" : "none",
        display: "flex", flexDirection: "column",
        maxHeight: chatOpen ? "50vh" : "auto",
        transition: "width 0.15s"
      }}>
        {/* Toggle bar */}
        <div
          onClick={() => setChatOpen(!chatOpen)}
          style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 14px",
            cursor: "pointer", userSelect: "none",
            background: "#1F3A2E", color: "#fff", borderTopLeftRadius: 8,
            fontSize: 13, fontWeight: 600
          }}
        >
          <span>{chatOpen ? "▼" : "▲"} DSL Generator</span>
          {generating && <span style={{ fontSize: 11, opacity: 0.7 }}>generating…</span>}
          {genError && <span style={{ fontSize: 11, color: "#F0A0A0" }}>error</span>}
        </div>

        {chatOpen && (
          <>
            {/* Messages */}
            <div style={{ flex: 1, overflow: "auto", padding: "10px 14px", display: "flex", flexDirection: "column", gap: 8, minHeight: 0, maxHeight: 300 }}>
              {chatMessages.length === 0 && (
                <div style={{ color: "#999", fontSize: 12, textAlign: "center", padding: 20 }}>
                  Describe the slide you want — e.g.<br/>
                  <em>"Add a pie chart of market share top right and a text block with key takeaways left"</em><br/>
                  or <em>"Switch the bar chart to a line chart and make it smaller"</em><br/>
                  <strong style={{ color: "#BA7517" }}>Tip:</strong> Click a block on the canvas first to scope changes to that block.
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} style={{
                  alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                  maxWidth: "90%",
                  background: msg.error ? "#ffe0e0" : msg.role === "user" ? "#1F3A2E" : "#f0f4ff",
                  color: msg.error ? "#a00" : msg.role === "user" ? "#fff" : "#333",
                  padding: "8px 12px", borderRadius: 8, fontSize: 12, lineHeight: 1.4
                }}>
                  <div>{msg.text}</div>
                </div>
              ))}
            </div>

            {/* Selected block indicator */}
            {clickedBlockId && (
              <div style={{
                padding: "4px 14px", background: "#FFF3E0", borderTop: "1px solid #FFCC80",
                fontSize: 11, color: "#E65100", display: "flex", alignItems: "center", gap: 4
              }}>
                <span style={{ fontWeight: 600 }}>✧</span>
                Block <strong>{clickedBlockId}</strong> selected — edits will be scoped to this block only
              </div>
            )}

            {/* Input row */}
            <div style={{ display: "flex", gap: 6, padding: "8px 14px", borderTop: "1px solid #eee" }}>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleGenerateDSL(); } }}
                placeholder={clickedBlockId ? "Modify the selected block…" : "Describe the slide or modification…"}
                disabled={generating}
                style={{
                  flex: 1, padding: "6px 10px", fontSize: 12, borderRadius: 4,
                  border: "1px solid #ccc", outline: "none",
                  fontFamily: "inherit"
                }}
              />
              <button
                onClick={handleGenerateDSL}
                disabled={generating || !chatInput.trim()}
                style={{
                  padding: "6px 14px", fontSize: 12, borderRadius: 4,
                  border: "none", cursor: generating || !chatInput.trim() ? "not-allowed" : "pointer",
                  background: generating || !chatInput.trim() ? "#aaa" : "#1F3A2E",
                  color: "#fff", whiteSpace: "nowrap"
                }}
              >
                {generating ? "…" : "Generate"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ── evidence hover tooltip (positioned by stage handlers) ── */}
      <div
        ref={tooltipRef}
        style={{
          position: "fixed", display: "none", zIndex: 2000, maxWidth: 360,
          background: "#1F3A2E", color: "#fff", padding: "8px 10px", borderRadius: 6,
          fontSize: 12, lineHeight: 1.4, boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
          pointerEvents: "none"
        }}
      />

      {/* ── fullscreen inspector popover ── */}
      {popover && (
        <div
          onClick={() => setPopover(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 6, width: "min(1100px, 92vw)", height: "88vh",
              display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.35)"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #eee" }}>
              <strong style={{ fontSize: 13 }}>{popover.title}</strong>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => navigator.clipboard?.writeText(popover.content)}
                  style={{ fontSize: 12, padding: "4px 10px", cursor: "pointer", border: "1px solid #ccc", borderRadius: 4, background: "#fff" }}
                >
                  Copy
                </button>
                <button
                  onClick={() => setPopover(null)}
                  style={{ fontSize: 12, padding: "4px 10px", cursor: "pointer", border: "none", borderRadius: 4, background: "#1F3A2E", color: "#fff" }}
                >
                  Close ✕
                </button>
              </div>
            </div>
            <textarea
              readOnly
              style={{ flex: 1, width: "100%", border: "none", outline: "none", padding: 14, fontFamily: "monospace", fontSize: 12, resize: "none", whiteSpace: "pre", overflow: "auto", boxSizing: "border-box" }}
              value={popover.content}
            />
          </div>
        </div>
      )}
    </div>
  );
}
